import React, { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import StartTrialCta from "../ui/StartTrialCta";

const navigation = [
    { name: "Home", href: "/" },
    { name: "About", href: "/about" },
    { name: "Try Demo", href: "/demo" },
    { name: "Blog", href: "/blog" },
    { name: "Contact", href: "/contact" },
];

function Logo() {
    return (
        <Link to="/" className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-gray-900 flex items-center justify-center">
                <span className="text-white font-semibold text-sm">O</span>
            </div>
            <span className="font-semibold text-gray-900 tracking-tight">OmniWorth</span>
        </Link>
    );
}

function Navbar() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const { isAuthenticated } = useAuth();

    return (
        <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-200">
            <nav className="max-w-6xl mx-auto px-4 sm:px-6">
                <div className="flex items-center justify-between h-16">
                    <Logo />

                    <div className="hidden md:flex items-center gap-7">
                        {navigation.map((item) => (
                            <NavLink
                                key={item.name}
                                to={item.href}
                                end={item.href === "/"}
                                className={({ isActive }) =>
                                    `text-sm font-medium transition-colors ${
                                        isActive ? "text-gray-900" : "text-gray-500 hover:text-gray-900"
                                    }`
                                }
                            >
                                {item.name}
                            </NavLink>
                        ))}
                    </div>

                    <div className="hidden md:flex items-center gap-3">
                        {!isAuthenticated && (
                            <Link
                                to="/login"
                                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                            >
                                Log in
                            </Link>
                        )}
                        <StartTrialCta plan="monthly" className="btn-primary px-4 py-2 text-sm">
                            {isAuthenticated ? "Start free trial" : "Get started"}
                        </StartTrialCta>
                    </div>

                    <button
                        onClick={() => setMobileOpen(!mobileOpen)}
                        className="md:hidden p-2 text-gray-600 hover:text-gray-900"
                        aria-label="Toggle menu"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {mobileOpen ? (
                                <>
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </>
                            ) : (
                                <>
                                    <line x1="4" y1="12" x2="20" y2="12" />
                                    <line x1="4" y1="6" x2="20" y2="6" />
                                    <line x1="4" y1="18" x2="20" y2="18" />
                                </>
                            )}
                        </svg>
                    </button>
                </div>

                {mobileOpen && (
                    <div className="md:hidden py-4 border-t border-gray-200">
                        <div className="space-y-1">
                            {navigation.map((item) => (
                                <NavLink
                                    key={item.name}
                                    to={item.href}
                                    end={item.href === "/"}
                                    onClick={() => setMobileOpen(false)}
                                    className={({ isActive }) =>
                                        `block px-3 py-2 rounded-lg text-sm font-medium ${
                                            isActive
                                                ? "bg-gray-100 text-gray-900"
                                                : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                                        }`
                                    }
                                >
                                    {item.name}
                                </NavLink>
                            ))}
                        </div>
                        <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                            {!isAuthenticated && (
                                <Link
                                    to="/login"
                                    onClick={() => setMobileOpen(false)}
                                    className="block px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                                >
                                    Log in
                                </Link>
                            )}
                            <StartTrialCta plan="monthly" className="block w-full btn-primary py-2 text-sm text-center">
                                {isAuthenticated ? "Start free trial" : "Get started"}
                            </StartTrialCta>
                        </div>
                    </div>
                )}
            </nav>
        </header>
    );
}

function Footer() {
    return (
        <footer className="border-t border-gray-200 bg-white">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <Logo />
                    <div className="flex items-center gap-6">
                        <Link to="/privacy" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                            Privacy
                        </Link>
                        <Link to="/terms" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                            Terms
                        </Link>
                        <Link to="/security" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                            Security
                        </Link>
                        <Link to="/changelog" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                            Changelog
                        </Link>
                        <a
                            href="https://x.com/omniworth"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-500 hover:text-gray-900 transition-colors"
                            aria-label="OmniWorth on X"
                        >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                            </svg>
                        </a>
                    </div>
                </div>
                <div className="mt-6 text-sm text-gray-500">
                    &copy; {new Date().getFullYear()} OmniWorth LLC. Created by Jake Marotta.
                </div>
            </div>
        </footer>
    );
}

export default function PublicLayout({ children }) {
    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1">{children}</main>
            <Footer />
        </div>
    );
}
